import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, ArrowUpCircle, AudioLines, Crown, Loader2, Mic, Music2, RotateCw, Trash2, Upload, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Progress } from '../ui/progress';
import {
  absUrl, errMsg, generateAllVoices, removeMusic, sceneVoiceover, upgradeAllVoices, upgradeVoice, uploadMusic, voiceEngineHealth, voicesEstimate,
} from '../../lib/aiStudioApi';
import { jobRunning, jobStalled } from './useMovieProject';
import { cn } from '../../lib/utils';

const MODES = [
  ['cast', 'Cast voices', Mic, 'Each character keeps one consistent voice from your Cast. Recorded line by line and mixed in the Final Cut.'],
  ['native', 'Native AI audio', AudioLines, 'The video model speaks the lines and adds ambience inside each clip. Shots on models without sound fall back to Cast voices.'],
];
const PILL = {
  ready: ['Voice ready', 'bg-emerald-500/15 text-emerald-200'],
  generating: ['Recording…', 'bg-amber-500/15 text-amber-200'],
  pending: ['Waiting for your own voice', 'bg-sky-500/15 text-sky-200'],
  failed: ["Couldn't record — retry", 'bg-red-500/15 text-red-200'],
  native: ['In-clip audio', 'bg-teal-500/15 text-teal-200'],
  none: ['No voice yet', 'bg-neutral-800 text-neutral-400'],
};
const voiceState = (s, native) => (s.audio_url ? 'ready'
  : s.voice_status === 'pending' && !/training/i.test(s.voice_error || '') ? 'none'
    : PILL[s.voice_status] ? s.voice_status : native && s.audio_fallback !== 'cast' ? 'native' : 'none');

function EngineBadge({ scene }) {
  if (!scene.audio_url) return null;
  if (scene.voice_engine === 'elevenlabs') return <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] text-amber-200" data-testid={`voice-engine-${scene.id}`}>Premium voice</span>;
  if (!scene.voice_backup) return <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-300" data-testid={`voice-engine-${scene.id}`}>Main voice</span>;
  return (
    <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] text-sky-200" data-testid={`voice-engine-${scene.id}`}>
      Backup voice{scene.voice_approx && <span className="text-amber-300"> · approximate accent</span>}
    </span>
  );
}

function VoiceRow({ project, scene, n, perLine, mainUp, onChanged }) {
  const [busy, setBusy] = useState(false);
  const native = project.audio_mode === 'native';
  const st = voiceState(scene, native);
  const run = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); onChanged(); } };
  const record = () => run(() => sceneVoiceover(project.id, scene.id), 'Voice recorded');
  const upgrade = () => run(() => upgradeVoice(project.id, scene.id), 'Upgraded to the main voice');
  return (
    <div className="grid grid-cols-[28px_1fr] items-center gap-3 border-b border-neutral-900 px-3 py-2.5 last:border-0 md:grid-cols-[28px_1fr_auto_auto]" data-testid={`voice-row-${scene.id}`}>
      <span className="font-mono text-[11px] text-neutral-500">{String(n).padStart(2, '0')}</span>
      <div className="min-w-0">
        <div className="truncate text-xs text-neutral-200">
          {project.format === 'series' && <span className="mr-1.5 font-mono text-[10px] text-amber-300">EP{scene.episode || 1}</span>}
          <span className="font-medium">{scene.dialogue ? scene.speaker || 'Dialogue' : 'Narrator'}:</span> {scene.dialogue || scene.narration}
        </div>
        {scene.voice_error && ['failed', 'pending'].includes(st) && <div className="truncate text-[10px] text-red-300/80">{scene.voice_error}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 justify-self-start">
        <span className={cn('rounded-full px-2 py-0.5 text-[10px]', PILL[st][1])} data-testid={`voice-status-${scene.id}`}>{PILL[st][0]}</span>
        <EngineBadge scene={scene} />
      </div>
      <div className="flex items-center gap-2">
        {scene.audio_url && <audio src={absUrl(scene.audio_url)} controls preload="none" className="h-7 w-40" />}
        {scene.voice_backup && scene.audio_url && mainUp && (
          <Button size="sm" variant="outline" disabled={busy} onClick={upgrade} className="h-7 border-emerald-500/40 text-xs text-emerald-200" data-testid={`voice-upgrade-${scene.id}`}>
            <ArrowUpCircle size={12} /><span className="ml-1">Upgrade voice · free</span>
          </Button>
        )}
        {st !== 'native' && (
          <Button size="sm" variant="outline" disabled={busy || st === 'generating'} onClick={record} className="h-7 border-neutral-700 text-xs text-neutral-200" data-testid={`voice-record-${scene.id}`}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}<span className="ml-1">{scene.audio_url ? 'Redo' : 'Record'} · {perLine} cr</span>
          </Button>
        )}
      </div>
    </div>
  );
}

function MusicBed({ project, onChanged }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); onChanged(); } };
  const m = project.music;
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900/40 p-4" data-testid="music-bed">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-neutral-100"><Music2 size={15} className="text-amber-300" /> Music bed <span className="text-[11px] font-normal text-neutral-500">· auto-ducked under dialogue in the Final Cut · free</span></div>
      <input ref={ref} type="file" accept=".mp3,.wav,.m4a,.aac,.ogg,audio/*" className="hidden" data-testid="music-file-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) run(() => uploadMusic(project.id, f), 'Music added'); }} />
      {m ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="max-w-[240px] truncate text-xs text-neutral-300" data-testid="music-name">{m.name}</span>
          <audio src={absUrl(m.url)} controls preload="none" className="h-8" />
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => removeMusic(project.id), 'Music removed')} className="text-neutral-400 hover:text-red-300" data-testid="music-remove-btn"><Trash2 size={13} /></Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} className="border-neutral-700 text-neutral-200" data-testid="music-upload-btn">
          {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Upload size={13} className="mr-1" />} Upload MP3 / WAV / M4A (max 25 MB)
        </Button>
      )}
    </div>
  );
}

/** Step 6 — Sound: Native vs Cast voices, per-shot voice tracking, generate-all, music bed. */
export function SoundStep({ project, catalog, onPatch, onChanged }) {
  const [est, setEst] = useState(null);
  const [starting, setStarting] = useState(false);
  const native = project.audio_mode === 'native';
  const lines = (project.scenes || []).filter((s) => s.dialogue || s.narration);
  const voiced = lines.filter((s) => s.audio_url).length;
  const job = project.voices || {};
  const running = jobRunning(job) && !jobStalled(job);
  const [main, setMain] = useState('up');
  const backups = lines.filter((s) => s.audio_url && s.voice_backup).length;
  const up = project.voices_upgrade || {};
  const upgrading = jobRunning(up) && !jobStalled(up);
  useEffect(() => { voicesEstimate(project.id, 0).then(setEst).catch(() => setEst(null)); }, [project.id, project.audio_mode, project.premium_voices, voiced, lines.length]);
  useEffect(() => {
    const check = () => voiceEngineHealth().then((r) => setMain(r.main)).catch(() => {});
    check();
    const t = setInterval(check, 30000);
    return () => clearInterval(t);
  }, []);
  async function upgradeAll() {
    try { const r = await upgradeAllVoices(project.id); toast.success(`Upgrading ${r.lines} backup lines — free`); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  }
  async function all() {
    setStarting(true);
    try { const r = await generateAllVoices(project.id, 0); toast.success(`Recording ${r.lines} lines`); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setStarting(false); }
  }
  return (
    <div className="space-y-5" data-testid="sound-step">
      {main !== 'up' && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-2.5 text-xs text-amber-100" data-testid="main-voice-down-banner">
          <AlertTriangle size={14} className="shrink-0 text-amber-300" /> Main voice is down, using backup voices. Nothing is blank.
        </div>
      )}
      {main === 'up' && backups > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-xs text-emerald-100" data-testid="main-voice-back-banner">
          <span>Main voice is back · {backups} line{backups === 1 ? '' : 's'} still use a backup voice</span>
          {upgrading
            ? <span className="flex items-center gap-1.5 font-mono" data-testid="voices-upgrade-progress"><Loader2 size={12} className="animate-spin" /> Upgrading {up.done || 0}/{up.total}</span>
            : <Button size="sm" onClick={upgradeAll} className="h-7 bg-emerald-500 text-xs text-neutral-900 hover:bg-emerald-400" data-testid="voices-upgrade-all-btn"><ArrowUpCircle size={12} className="mr-1" /> Upgrade {backups} backup line{backups === 1 ? '' : 's'} (free)</Button>}
        </div>
      )}
      {up.status === 'stopped' && up.error && <div className="text-[11px] text-amber-300" data-testid="voices-upgrade-stopped">{up.error}</div>}
      <div className="grid gap-3 md:grid-cols-2" data-testid="audio-mode-switch">
        {MODES.map(([v, title, Icon, sub]) => (
          <button key={v} type="button" onClick={() => onPatch({ audio_mode: v })} data-testid={`audio-mode-${v}`}
            className={cn('rounded-xl border p-4 text-left transition-colors duration-200', (project.audio_mode || 'cast') === v ? 'border-amber-400 bg-amber-500/10' : 'border-neutral-800 bg-neutral-900/40 hover:border-neutral-600')}>
            <div className="flex items-center gap-2 text-sm font-semibold text-neutral-100"><Icon size={15} className="text-amber-300" /> {title}</div>
            <div className="mt-1 text-xs text-neutral-400">{sub}</div>
          </button>
        ))}
      </div>

      <label className="flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-neutral-800 bg-neutral-900/40 p-4" data-testid="premium-voices-card">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-100"><Crown size={15} className="text-amber-300" /> Premium voices
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-200">ElevenLabs · {catalog.pricing?.voiceover_premium ?? 6} cr per line</span>
          </div>
          <div className="mt-1 text-xs text-neutral-400">More natural, more emotional voices in 29 languages. If ElevenLabs can't voice a line, the normal voices take over and that line costs the usual {catalog.pricing?.voiceover ?? 3} cr.</div>
        </div>
        <Switch checked={!!project.premium_voices} onCheckedChange={(v) => onPatch({ premium_voices: v })} data-testid="premium-voices-switch" />
      </label>

      <div className="rounded-xl border border-neutral-800 bg-neutral-900/40">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-800 px-4 py-3">
          <div className="text-sm font-semibold text-neutral-100">Voice tracks <span className="ml-1 font-mono text-xs text-neutral-500" data-testid="voice-tracks-count">{voiced}/{lines.length} recorded</span></div>
          <Button size="sm" onClick={all} disabled={starting || running || !est?.lines} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="voices-generate-all-btn">
            <Wand2 size={13} className="mr-1" /> {est?.lines ? `Record ${est.lines} missing — ${est.credits} cr` : 'All lines voiced'}
          </Button>
        </div>
        {running && (
          <div className="space-y-1.5 border-b border-neutral-800 px-4 py-3" data-testid="voices-progress">
            <div className="flex justify-between text-xs"><span className="text-amber-100">Recording line {Math.min((job.done || 0) + 1, job.total || 1)} of {job.total}…</span><span className="font-mono text-neutral-400">{Math.round(((job.done || 0) / Math.max(1, job.total)) * 100)}%</span></div>
            <Progress value={((job.done || 0) / Math.max(1, job.total)) * 100} className="h-1.5 bg-neutral-800" />
          </div>
        )}
        {lines.length === 0 && <div className="p-6 text-center text-xs text-neutral-500">No dialogue or narration yet — write the screenplay first.</div>}
        {lines.map((s, i) => <VoiceRow key={s.id} project={project} scene={s} n={i + 1} perLine={est?.credits_per_line ?? catalog.pricing?.voiceover ?? '—'} mainUp={main === 'up'} onChanged={onChanged} />)}
        {native && lines.length > 0 && <div className="px-4 py-2 text-[11px] text-neutral-500">Native mode: only shots whose model has no sound need a Cast voice — the rest speak inside the clip.</div>}
      </div>

      <MusicBed project={project} onChanged={onChanged} />
    </div>
  );
}
